import React, { useState, useRef, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCreditCard, faEllipsisV, faPlus } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import {
  useGetPaymentMethodsQuery,
  useRemovePaymentMethodMutation,
  useSetDefaultPaymentMethodMutation,
  BillingPaymentMethod,
} from 'store/services/subscription/subscriptionApi';
import AddPaymentMethodModal from './AddPaymentMethodModal';
import './PaymentMethodsSection.css';

const BRAND_LABELS: Record<string, string> = {
  visa: 'Visa',
  mastercard: 'Mastercard',
  amex: 'Amex',
  discover: 'Discover',
  diners: 'Diners',
  jcb: 'JCB',
  unionpay: 'UnionPay',
};

interface Props {
  hasActiveSubscription: boolean;
}

const PaymentMethodsSection: React.FC<Props> = ({ hasActiveSubscription }) => {
  const { data, isLoading } = useGetPaymentMethodsQuery();
  const [removePaymentMethod] = useRemovePaymentMethodMutation();
  const [setDefaultPaymentMethod] = useSetDefaultPaymentMethodMutation();
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const handleCloseAddModal = useCallback(() => setShowAddModal(false), []);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSetDefault = async (id: string) => {
    setOpenMenuId(null);
    try {
      await setDefaultPaymentMethod(id).unwrap();
      toast.success('Default payment method updated.');
    } catch {
      toast.error('Failed to update default payment method.');
    }
  };

  const handleRemove = async (pm: BillingPaymentMethod) => {
    setOpenMenuId(null);
    const isLastMethod = data?.length === 1;
    if (isLastMethod && hasActiveSubscription) {
      toast.error('Cannot remove your only payment method while you have an active subscription.');
      return;
    }
    try {
      await removePaymentMethod(pm.id).unwrap();
      toast.success(`Card ending in ${pm.last4} removed.`);
    } catch {
      toast.error('Failed to remove payment method.');
    }
  };

  const getBrandLabel = (brand: string) => {
    return BRAND_LABELS[brand.toLowerCase()] || brand.charAt(0).toUpperCase() + brand.slice(1);
  };

  const paymentMethods = data || [];

  return (
    <div className="billing-pm-section">
      <div className="billing-pm-header">
        <h4 className="billing-pm-title">Payment Methods</h4>
        <button className="billing-pm-add-btn" onClick={() => setShowAddModal(true)}>
          <FontAwesomeIcon icon={faPlus} />
          <span>Add</span>
        </button>
      </div>

      {isLoading ? (
        <div className="billing-pm-loading">Loading payment methods...</div>
      ) : paymentMethods.length === 0 ? (
        <div className="billing-pm-empty">No payment methods on file.</div>
      ) : (
        <div className="billing-pm-list">
          {paymentMethods.map((pm) => (
            <div key={pm.id} className="billing-pm-row">
              <div className="billing-pm-info">
                <FontAwesomeIcon
                  icon={faCreditCard}
                  className="billing-pm-brand-icon"
                />
                <span className="billing-pm-brand-label">{getBrandLabel(pm.brand)}</span>
                <span className="billing-pm-number">
                  •••• {pm.last4}
                </span>
                <span className="billing-pm-exp">
                  {String(pm.expMonth).padStart(2, '0')}/{String(pm.expYear).slice(-2)}
                </span>
                {pm.isDefault && (
                  <span className="billing-pm-default-badge">Default</span>
                )}
              </div>

              <div className="billing-pm-actions" ref={openMenuId === pm.id ? menuRef : null}>
                <button
                  className="billing-pm-menu-btn"
                  onClick={() => setOpenMenuId(openMenuId === pm.id ? null : pm.id)}
                >
                  <FontAwesomeIcon icon={faEllipsisV} />
                </button>

                {openMenuId === pm.id && (
                  <div className="billing-pm-dropdown">
                    {!pm.isDefault && (
                      <button
                        className="billing-pm-dropdown-item"
                        onClick={() => handleSetDefault(pm.id)}
                      >
                        Set as Default
                      </button>
                    )}
                    <button
                      className="billing-pm-dropdown-item billing-pm-dropdown-item--danger"
                      onClick={() => handleRemove(pm)}
                    >
                      Remove
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <AddPaymentMethodModal
        isOpen={showAddModal}
        onClose={handleCloseAddModal}
      />
    </div>
  );
};

export default PaymentMethodsSection;
